// VehicleTimeline past-trip loading + persistence tuning. Lives in the trips slice (not
// the component layer) so BOTH the thunk and the redux-persist transform in RootReducer
// can import it without a component → redux dependency.

// Fetch the past-trip window in date sub-ranges, CLOSEST to today first, so the most
// relevant tiles render before older ones stream in. Values are cumulative days back
// from now; the last MUST reach the rendered range start (DAYS_BEFORE_TODAY = 90).
// Chunk i covers [now − CHUNK[i], now − CHUNK[i−1]]; chunk 0's upper bound is `now` (no
// future buffer — finished trips are always in the past). First chunk = the closest 1 week
// (fast first paint of the most recent trips).
export const TIMELINE_TRIP_CHUNK_DAYS = [7, 14, 45, 90];

// Progressive page size: the first TIMELINE_TRIP_FAST_BATCHES requests use a SMALL page so
// the closest days' trips render almost immediately (tiny, fast response); after that we
// switch to the big page size to bulk-load the older window in fewer round-trips.
export const TIMELINE_TRIP_PAGE_SIZE_FIRST = 50;
export const TIMELINE_TRIP_FAST_BATCHES = 2;
export const TIMELINE_TRIP_PAGE_SIZE = 500;

// Only the last N days of past trips are PERSISTED (redux-persist → localStorage), trimmed
// to the tile fields, so the userData blob can't blow the ~5MB quota. The in-memory set
// still holds the full window; only the persisted copy is bounded.
export const TIMELINE_PERSIST_DAYS = 14;

// The only fields the timeline tiles need (see useTimelineData mapping + bookingTiles):
// id/carId place the tile on a row; the dates size it; status/type/code style + label it.
export const TIMELINE_TRIP_FIELDS = [
  'id',
  'carId',
  'tripStart',
  'tripEnd',
  'backBy',
  'reservedAt',
  'status',
  'type',
  'code',
];

// Minimal projection of a trip for persistence (drops user PII, records, pickupLocation…).
export const trimTripForTimeline = (t: any): any => {
  if (!t) return t;
  const out: any = {};
  TIMELINE_TRIP_FIELDS.forEach((k) => {
    if (t[k] !== undefined) out[k] = t[k];
  });
  return out;
};

// Keep only trips whose start (or reservation) is within the last `TIMELINE_PERSIST_DAYS`,
// trimmed. Used by the persist transform (inbound) to bound the localStorage blob.
export const boundTimelineTripsForPersist = (
  trips: any[] | null | undefined
): any[] => {
  if (!Array.isArray(trips) || !trips.length) return [];
  const cutoff = Date.now() - TIMELINE_PERSIST_DAYS * 24 * 60 * 60 * 1000;
  return trips
    .filter((t) => {
      const ms = Date.parse(t?.tripStart || t?.reservedAt || '');
      return !Number.isNaN(ms) && ms >= cutoff;
    })
    .map(trimTripForTimeline);
};
